import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Building2, ArrowLeft, ShieldCheck } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────
const SALES_PARTNER_PREFIX = 'EXISALES';

export const isSalesPartnerCollege = (leadAmbassadorId) =>
  Boolean(leadAmbassadorId?.toUpperCase().startsWith(SALES_PARTNER_PREFIX));

// ─── EnterpriseSalesContactPage ───────────────────────────────────────────────
// Shown to colleges whose lead_ambassador_id starts with EXISALES.
// These institutions are managed exclusively through the Enterprise Sales Network
// and must not be presented with any self-serve purchase UI.
// ─────────────────────────────────────────────────────────────────────────────

const contactCards = [
  {
    icon: Phone,
    heading: 'Call Enterprise Sales',
    value: '+91 62815 97586',
    href: 'tel:+916281597586',
    color: '#4F46E5',
    bg: 'rgba(79,70,229,0.06)',
    border: 'rgba(79,70,229,0.14)',
  },
  {
    icon: Mail,
    heading: 'Email Enterprise Sales',
    value: 'connect@evolvexinnovations.org',
    href: 'mailto:connect@evolvexinnovations.org',
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.06)',
    border: 'rgba(124,58,237,0.14)',
  },
  {
    icon: MapPin,
    heading: 'Corporate Office',
    value: 'Bhoomatha Thalluri Heights, Kankipadu, Vijayawada – 521151',
    href: null,
    color: '#0F172A',
    bg: 'rgba(15,23,42,0.04)',
    border: 'rgba(15,23,42,0.10)',
  },
];

const EnterpriseSalesContactPage = () => {
  const navigate = useNavigate();
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const username = user?.username || user?.userId;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F8F5FF 0%, #F0EBFF 40%, #FAFAFE 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 20px',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>
      <style>{`
        @keyframes sp-fadein {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .sp-card-hover {
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .sp-card-hover:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(79,70,229,0.14) !important;
        }
        .sp-back-btn:hover { opacity: 0.75; }
      `}</style>

      <div style={{
        width: '100%',
        maxWidth: 700,
        animation: 'sp-fadein 0.5s ease both',
      }}>

        {/* Back button */}
        {username && (
          <button
            className="sp-back-btn"
            onClick={() => navigate(`/collegehome/${username}`)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600, color: '#64748B',
              marginBottom: 24, padding: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
        )}

        {/* Main card */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 24,
          border: '1.5px solid rgba(99,102,241,0.12)',
          boxShadow: '0 8px 48px rgba(79,70,229,0.10)',
          overflow: 'hidden',
        }}>

          {/* Header band */}
          <div style={{
            background: 'linear-gradient(135deg, #312E81, #5B21B6, #7C3AED)',
            padding: '40px 44px 36px',
            textAlign: 'center',
          }}>
            <div style={{
              width: 72, height: 72,
              borderRadius: 20,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              border: '1.5px solid rgba(255,255,255,0.22)',
            }}>
              <Building2 size={34} color="#fff" />
            </div>
            <h1 style={{
              margin: '0 0 12px',
              fontSize: 26,
              fontWeight: 900,
              color: '#FFFFFF',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}>
              Contact Enterprise Sales
            </h1>
            <p style={{
              margin: 0,
              fontSize: 15,
              color: 'rgba(255,255,255,0.82)',
              lineHeight: 1.7,
              maxWidth: 480,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              Your institution is registered under an authorized Sales Partner program.
              Subscription activation and pricing are managed exclusively through your
              assigned enterprise representative.
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: '36px 44px 40px' }}>

            <p style={{
              fontSize: 15,
              color: '#475569',
              lineHeight: 1.75,
              textAlign: 'center',
              marginBottom: 32,
              marginTop: 0,
            }}>
              Please contact our Enterprise Sales Team to complete your subscription process.
            </p>

            {/* Contact cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
              {contactCards.map(({ icon: Icon, heading, value, href, color, bg, border }) => {
                const inner = (
                  <div
                    className="sp-card-hover"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 18,
                      padding: '20px 24px',
                      borderRadius: 16,
                      background: bg,
                      border: `1.5px solid ${border}`,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                      cursor: href ? 'pointer' : 'default',
                      textDecoration: 'none',
                    }}
                  >
                    <div style={{
                      width: 44, height: 44, borderRadius: 12,
                      background: '#FFFFFF',
                      border: `1.5px solid ${border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={20} color={color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        margin: '0 0 4px',
                        fontSize: 12, fontWeight: 700,
                        color: '#94A3B8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.07em',
                      }}>
                        {heading}
                      </p>
                      <p style={{
                        margin: 0,
                        fontSize: 16,
                        fontWeight: 800,
                        color,
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                      }}>
                        {value}
                      </p>
                    </div>
                  </div>
                );

                return href ? (
                  <a key={heading} href={href} style={{ textDecoration: 'none', display: 'block' }}>
                    {inner}
                  </a>
                ) : (
                  <div key={heading}>{inner}</div>
                );
              })}
            </div>

            {/* Info box */}
            <div style={{
              background: 'rgba(79,70,229,0.04)',
              border: '1.5px solid rgba(79,70,229,0.12)',
              borderRadius: 16,
              padding: '20px 24px',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                marginBottom: 10,
              }}>
                <ShieldCheck size={18} color="#4F46E5" />
                <p style={{
                  margin: 0,
                  fontSize: 14, fontWeight: 800,
                  color: '#312E81',
                  letterSpacing: '-0.01em',
                }}>
                  Enterprise Subscription Process
                </p>
              </div>
              <p style={{
                margin: 0,
                fontSize: 14,
                color: '#475569',
                lineHeight: 1.75,
              }}>
                Your institution is managed through our Enterprise Sales Network. To ensure
                customized pricing, onboarding assistance, and institutional agreements,
                subscription activation is processed directly by our Enterprise Sales Team.
                Please contact the details above for further assistance.
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseSalesContactPage;
